// ═══════════════════════════════════════════════════════════════
// lmsApi — client REST du module LMS (/api/lms). Bearer JWT.
// ═══════════════════════════════════════════════════════════════
const BASE = '/api/lms';
const token = () => localStorage.getItem('oracle_token');
const H = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

async function req(method, path, body) {
    const res = await fetch(`${BASE}${path}`, {
        method, headers: H(), body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const e = await res.json().catch(() => ({ error: `Erreur ${res.status}` }));
        const err = new Error(e.error || `Erreur ${res.status}`);
        err.code = e.code; err.status = res.status; err.payload = e;
        throw err;
    }
    return res.status === 204 ? null : res.json();
}

export const lmsApi = {
    // Catalogue & apprentissage
    listPublished: (tenant) => req('GET', `/courses${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ''}`),
    getCourseBySlug: (slug) => req('GET', `/courses/slug/${encodeURIComponent(slug)}`),
    myEnrollments: () => req('GET', '/enrollments/me'),
    enroll: (courseId, source = 'direct') => req('POST', `/courses/${courseId}/enroll`, { source }),
    checkout: (courseId) => req('POST', `/courses/${courseId}/checkout`),
    getProgress: (courseId) => req('GET', `/courses/${courseId}/progress`),
    completeLesson: (lessonId, data = {}) => req('POST', `/lessons/${lessonId}/complete`, data),

    // Tuteur IA de leçon : historique persistant + persistance d'une paire Q/R
    getTutorMessages: (lessonId) => req('GET', `/lessons/${lessonId}/tutor/messages`),
    saveTutorMessage: (lessonId, data) => req('POST', `/lessons/${lessonId}/tutor/messages`, data),

    // Média de leçon : URL de lecture vidéo (signée H2 ou externe) + ouverture du support fichier
    getLessonVideoUrl: (lessonId) => req('GET', `/lessons/${lessonId}/video-url`),
    // Le support fichier passe par un endpoint authentifié → on récupère un blob et un objectURL.
    fetchLessonFileBlobUrl: async (lessonId) => {
        const res = await fetch(`${BASE}/lessons/${lessonId}/file`, { headers: { Authorization: `Bearer ${token()}` } });
        if (!res.ok) {
            const e = await res.json().catch(() => ({ error: `Erreur ${res.status}` }));
            throw new Error(e.error || `Erreur ${res.status}`);
        }
        if (res.redirected) return res.url; // file_ref = URL externe
        return URL.createObjectURL(await res.blob());
    },
    // Téléchargement d'une ressource de leçon (transcript, slides…) → objectURL (ou URL externe).
    fetchResourceBlobUrl: async (lessonId, resId) => {
        const res = await fetch(`${BASE}/lessons/${lessonId}/resources/${encodeURIComponent(resId)}/download`,
            { headers: { Authorization: `Bearer ${token()}` } });
        if (!res.ok) {
            const e = await res.json().catch(() => ({ error: `Erreur ${res.status}` }));
            throw new Error(e.error || `Erreur ${res.status}`);
        }
        if (res.redirected) return res.url;
        return URL.createObjectURL(await res.blob());
    },

    // Évaluations
    getAssessment: (id) => req('GET', `/assessments/${id}`),
    attempt: (assessmentId, answers) => req('POST', `/assessments/${assessmentId}/attempt`, { answers }),
    myAttempts: (courseId) => req('GET', `/courses/${courseId}/attempts/me`),

    // Certificat
    issueCertificate: (courseId) => req('POST', `/courses/${courseId}/certificate`),
    verifyCertificate: (uid) => fetch(`${BASE}/certificates/verify/${encodeURIComponent(uid)}`).then(r => r.json()),

    // Authoring (formateur)
    myCourses: () => req('GET', '/courses/mine'),
    createCourse: (data) => req('POST', '/courses', data),
    updateCourse: (id, data) => req('PUT', `/courses/${id}`, data),
    publishCourse: (id, publish = true) => req('POST', `/courses/${id}/publish`, { publish }),
    deleteCourse: (id) => req('DELETE', `/courses/${id}`),
    addModule: (courseId, data) => req('POST', `/courses/${courseId}/modules`, data),
    updateModule: (moduleId, data) => req('PUT', `/modules/${moduleId}`, data),
    reorderModules: (courseId, orderedIds) => req('PUT', `/courses/${courseId}/modules/reorder`, { ordered_ids: orderedIds }),
    deleteModule: (moduleId) => req('DELETE', `/modules/${moduleId}`),
    addLesson: (moduleId, data) => req('POST', `/modules/${moduleId}/lessons`, data),
    updateLesson: (id, data) => req('PUT', `/lessons/${id}`, data),
    deleteLesson: (id) => req('DELETE', `/lessons/${id}`),
    reorderLessons: (moduleId, orderedIds) => req('PUT', `/modules/${moduleId}/lessons/reorder`, { ordered_ids: orderedIds }),
    attachBase: (courseId, baseId, role = 'primary') => req('POST', `/courses/${courseId}/bases`, { base_id: baseId, role }),
    detachBase: (courseId, baseId) => req('DELETE', `/courses/${courseId}/bases/${baseId}`),
    listCourseInstructors: (courseId) => req('GET', `/courses/${courseId}/instructors`),
    addCourseInstructor: (courseId, user) => req('POST', `/courses/${courseId}/instructors`, { user }),
    removeCourseInstructor: (courseId, userId) => req('DELETE', `/courses/${courseId}/instructors/${encodeURIComponent(userId)}`),
    listAssessments: (courseId) => req('GET', `/courses/${courseId}/assessments`),
    createAssessment: (courseId, data) => req('POST', `/courses/${courseId}/assessments`, data),
    updateAssessment: (id, data) => req('PUT', `/assessments/${id}`, data),

    // Bases d'expertise accessibles (pour le sélecteur d'attache) — réutilise l'API expertise
    listBases: () => fetch('/api/expertise-bases', { headers: { Authorization: `Bearer ${token()}` } })
        .then(r => (r.ok ? r.json() : []))
        .then(list => (Array.isArray(list) ? list : []))
        .catch(() => []),

    // Fichiers du projet backing d'un cours (pour le sélecteur de leçon « fichier ») — API projets
    listProjectFiles: (projectId, limit = 100) =>
        fetch(`/api/projects/${projectId}/files?limit=${limit}`, { headers: { Authorization: `Bearer ${token()}` } })
            .then(r => (r.ok ? r.json() : { files: [] }))
            .then(d => (Array.isArray(d.files) ? d.files : []))
            .catch(() => []),
};

export default lmsApi;
